import Image from 'next/image'
import { useTranslation } from 'src/utils/i18n'
import dayjs from 'dayjs'
import { Modal, Spin, message } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector, useDispatch } from 'react-redux'
import { getPaymentInfo, upgradePayment } from 'src/@api/dashboard'
import { resolveUpgradeDeviceId } from 'src/utils/resolveUpgradeDeviceId'
import { refreshSubscriptionExpire } from 'src/utils/refreshSubscriptionExpire'
import { changeExtremeService } from 'src/store/apps/Common'
import { notifyExtremeUpgraded } from 'src/constants/extremeUpgrade'
import { messageInfo } from 'src/components/Message'
import { navigateTo } from 'src/utils/navigate'

const defaultPaymentInfo = {
  card: null,
  planText: ' -1-month ',
  expireTime: '2099-12-31',
  price: 49.98,
  upgradePrice: 44.98,
  renewPrice: 99.96
}

const ExtremeModal = ({ extremeOpen, setExtremeOpen, zIndex = 1200 }) => {
  const { t, locale } = useTranslation('common')
  const dispatch = useDispatch()
  const [messageApi, contextHolder] = message.useMessage({ prefixCls: 'my-message' })
  const [mounted, setMounted] = useState(false)

  const selectedDeviceId = useSelector(state => state.Common.deviceUser)

  const [deviceId, setDeviceId] = useState(null)
  const [checkID, setCheckID] = useState(1)
  const [tipsOpen, setTipsOpen] = useState(false)
  const [paymentInfo, setPaymentInfo] = useState(defaultPaymentInfo)
  const [confirming, setConfirming] = useState(false)
  const confirmInFlightRef = useRef(false)

  const handlerPlanText = name => {
    const match = name.match(/\d+/g)
    const months = match ? Number(match[0]) : 1

    return months >= 3 ? ` -${months}-${t('months')} ` : ` -${months}-${t('month')} `
  }

  const fatchPaymentInfo = async id => {
    if (!id) {
      setPaymentInfo(defaultPaymentInfo)
      setCheckID(1)

      return
    }

    try {
      const result = await getPaymentInfo({
        params: { id }
      })

      const data = result.data

      if (data) {
        const card = typeof data.card === 'string' ? data.card.trim() : ''
        setPaymentInfo({
          card: card || null,
          planText: handlerPlanText(data.name),
          expireTime: dayjs().add(Number(data.valid_days), 'day').format('YYYY-MM-DD'),
          price: Math.ceil(((Number(data.price) + Number(data.setup)) / 0.9) * 100) / 100,
          upgradePrice: Math.ceil((Number(data.price) + Number(data.setup)) * 100) / 100,
          renewPrice: data.renew_price
        })

        setCheckID(card ? 0 : 1)
      }
    } catch (e) {
      setPaymentInfo(defaultPaymentInfo)
      setCheckID(1)
      console.error(e.message)
    }
  }

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!extremeOpen) return

    const id = resolveUpgradeDeviceId(selectedDeviceId)
    setDeviceId(id)
    fatchPaymentInfo(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extremeOpen, selectedDeviceId])

  const getCheckoutHref = (id = deviceId) => {
    const currentId = id ?? resolveUpgradeDeviceId(selectedDeviceId)
    const lang = !locale || locale === '' ? 'en' : locale === 'it-ch' ? 'it' : locale
    const clientUrl = (process.env.NEXT_PUBLIC_CLIENT_URL || '').replace(/\/$/, '')

    return lang === 'en'
      ? `${clientUrl}/payment-2?id=${currentId}`
      : `${clientUrl}/${lang}/payment-2?id=${currentId}`
  }

  const onClickAddPayment = () => {
    const id = resolveUpgradeDeviceId(selectedDeviceId)
    if (!id) {
      messageInfo('Current device is missing')

      return
    }
    setDeviceId(id)
    setCheckID(1)
    navigateTo(getCheckoutHref(id))
  }

  const onClickConfirm = async () => {
    // Guard synchronously so rapid pointer events cannot start two payments
    // before React has committed the disabled state.
    if (confirmInFlightRef.current) return
    confirmInFlightRef.current = true

    try {
      const id = resolveUpgradeDeviceId(selectedDeviceId)
      if (!id) {
        messageInfo('Current device is missing')

        return
      }

      setDeviceId(id)

      if (checkID === 1 || !paymentInfo.card) {
        navigateTo(getCheckoutHref(id))

        return
      }

      setConfirming(true)

      const result = await upgradePayment({ id })
      if (Number(result.code) === 0) {
        await refreshSubscriptionExpire({ dispatch })
        dispatch(changeExtremeService(true))
        // spyx 对齐：先 USER_SUMMARY 预缓冲，再广播让本页拉详情读 advanced
        await notifyExtremeUpgraded()
        setExtremeOpen(false)
        messageApi.success(result.msg || 'Upgrade successful')
      } else {
        messageInfo(result?.msg || 'Upgrade failed', 6)
      }
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.msg || e?.message || 'Upgrade failed'
      messageInfo(errMsg, 6)
      console.error(errMsg)
    } finally {
      setConfirming(false)
      confirmInFlightRef.current = false
    }
  }

  const cardDisplay = paymentInfo.card
    ? paymentInfo.card.includes('*')
      ? paymentInfo.card
      : `**** **** **** ${paymentInfo.card.slice(-4)}`
    : '**** **** **** ****'

  const paymentOptionClass = selected =>
    `flex w-full cursor-pointer items-center rounded-[8px] border border-solid bg-white p-[10px] ${
      selected ? 'border-[#4A77FE]' : 'border-[#E9EFF3] hover:border-[#C8D5FF]'
    }`

  const overlay =
    extremeOpen && mounted
      ? createPortal(
          <div
            className='fixed bottom-0 right-0 top-[60px] flex items-center justify-center overflow-y-auto bg-[rgba(0,0,0,0.5)] px-4 py-6 smj:left-0 smj:items-start smj:px-4 smj:py-8 smjs:left-[300px]'
            style={{ zIndex }}
          >
            <div className='relative my-auto flex w-full max-w-[544px] flex-col items-center gap-8 overflow-hidden rounded-[16px] bg-gradient-to-b from-white to-[#E8F4FF] p-8 shadow-[0px_20px_30px_0px_rgba(40,41,56,0.3)] smj:max-h-[min(710px,calc(100vh-64px))] smj:max-w-[calc(100vw-32px)] smj:overflow-y-auto smj:p-5'>
              <button
                type='button'
                aria-label='close'
                className='absolute right-3 top-3 z-[1] flex size-6 items-center justify-center'
                onClick={() => setExtremeOpen(false)}
              >
                <span className='relative block size-[16px]' aria-hidden>
                  <span className='absolute left-1/2 top-1/2 block h-5 w-0.5 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-[4px] bg-[#9297AF]' />
                  <span className='absolute left-1/2 top-1/2 block h-5 w-0.5 -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-[4px] bg-[#9297AF]' />
                </span>
              </button>

              <h2 className='w-full text-center text-[24px] font-semibold leading-9 text-[#020417] smj:text-[20px] smj:leading-7'>
                {t('Choose your payment method')}
              </h2>

              <div className='flex w-full flex-col gap-4'>
                {paymentInfo.card ? (
                  <div
                    className={paymentOptionClass(checkID === 0)}
                    onClick={() => {
                      setCheckID(0)
                    }}
                  >
                    <span className='flex items-center gap-2.5'>
                      <Image
                        src='/extreme/card.webp'
                        alt=''
                        width={24}
                        height={24}
                        className='shrink-0 !h-6 !w-6'
                      />
                      <span className='text-[14px] font-medium leading-[18px] text-[#4A77FE]'>{cardDisplay}</span>
                    </span>
                  </div>
                ) : null}

                <div className={paymentOptionClass(checkID === 1)} onClick={onClickAddPayment}>
                  <span className='flex min-w-0 items-center gap-2.5'>
                    <Image
                      src='/extreme/add-unit.svg'
                      alt=''
                      width={24}
                      height={24}
                      className='shrink-0 !h-6 !w-6'
                    />
                    <span className='min-w-0 text-[14px] font-normal leading-[18px] text-[#020417]'>
                      {t('Add another card or payment method')}
                    </span>
                  </span>
                </div>
              </div>

              <div className='flex w-full flex-col gap-4'>
                <h3 className='text-[18px] font-medium leading-[26px] text-[#020417]'>{t('Order details')}:</h3>

                <div className='flex w-full flex-col gap-2.5'>
                  <div className='flex items-end justify-between gap-3 text-[14px]'>
                    <span className='shrink-0 leading-5 text-[#6B6F88]'>{t('Youre getting')}:</span>
                    <span className='text-right leading-[18px] text-[#020417]'>{t('Upgrade to')}&nbsp;Extreme</span>
                  </div>

                  <div className='flex items-start justify-between gap-3 text-[14px]'>
                    <span className='shrink-0 leading-5 text-[#6B6F88]'>{t('You Plan')}:</span>
                    <span className='text-right leading-[18px] text-[#020417]'>
                      {t('Msafely')}&nbsp;Extreme{paymentInfo.planText}
                      {t('Subscription')}
                    </span>
                  </div>

                  <div className='flex items-center justify-between gap-3 text-[14px]'>
                    <span className='flex shrink-0 items-center leading-5 text-[#6B6F88]'>
                      {t('Expire Time')}
                      <button type='button' className='mx-1 inline-flex size-3 shrink-0 items-center justify-center' onClick={() => setTipsOpen(true)}>
                        <Image
                          src='/extreme/question.svg'
                          alt=''
                          width={12}
                          height={12}
                          className='!h-3 !w-3'
                        />
                      </button>
                      :
                    </span>
                    <span className='text-right leading-[18px] text-[#020417]'>{paymentInfo.expireTime}</span>
                  </div>

                  <div className='flex items-center justify-between gap-3 text-[14px]'>
                    <span className='shrink-0 leading-5 text-[#6B6F88]'>{t('Upgrade price')}:</span>
                    <div className='flex items-start gap-1'>
                      <Image
                        src='/extreme/save10.webp'
                        alt=''
                        width={60}
                        height={20}
                        className='!h-5 !w-[60px] shrink-0'
                      />
                      <p className='whitespace-nowrap text-right text-[14px] leading-[21px] text-[#020417]'>
                        <span className='text-[#353746] line-through'>${paymentInfo.price}</span>
                        {' '}
                        <span className='font-medium'>${paymentInfo.upgradePrice}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className='flex w-full items-start justify-center gap-4 font-medium smj:flex-col'>
                <button
                  type='button'
                  className='flex h-10 w-[240px] items-center justify-center rounded-[40px] border border-solid border-[#454CFF] bg-transparent px-10 text-[14px] leading-5 text-[#454CFF] smj:order-1 smj:w-full'
                  onClick={() => setExtremeOpen(false)}
                >
                  {t('Cancel')}
                </button>
                <button
                  type='button'
                  disabled={confirming}
                  className='flex h-10 w-[240px] items-center justify-center rounded-[40px] bg-[#FFCF55] px-10 text-[14px] leading-5 text-[#020417] disabled:cursor-not-allowed disabled:opacity-70 smj:order-0 smj:w-full'
                  onClick={onClickConfirm}
                >
                  {confirming ? <Spin size='small' /> : t('Confirm')}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null

  return (
    <>
      {contextHolder}
      {overlay}

      <Modal
        open={tipsOpen}
        centered
        closable={false}
        footer={null}
        width='min(335px, calc(100vw - 32px))'
        zIndex={zIndex + 100}
        styles={{
          content: {
            padding: '0px',
            borderRadius: '4px'
          }
        }}
      >
        <div className='relative h-[38px] [border-bottom:1px_solid_#E6E6E6]'>
          <Image
            src='/extreme/extreme-close.svg'
            alt='extreme-close'
            width={16}
            height={16}
            className='absolute right-3 top-3 !h-4 !w-4 cursor-pointer'
            onClick={() => setTipsOpen(false)}
          />
        </div>
        <ol className='flex flex-col gap-y-[10px] p-3 text-[13px] font-normal leading-[18px] text-[#222222]'>
          <li>
            {t(
              'Your subscription date will be recalculated from the day of the upgrade so your subscription will be renewed on'
            )}{' '}
            <span className='text-[#2A99FF]'>{paymentInfo.expireTime}</span>.
          </li>
          <li>
            {t(
              'Your renewal price will be the sum of the Basic plan and the Extreme plan so your renewal price will be'
            )}{' '}
            <span className='text-[#2A99FF]'>${paymentInfo.renewPrice}</span>.
          </li>
        </ol>
      </Modal>
    </>
  )
}

export default ExtremeModal

